// src/routes/PrivateRoutes.tsx
import React, { useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import PrivateRoute from "./PrivateRouteWrapper";
import AppLayout from "../layouts/AppLayout";
import { useAppSelector } from "@redux/hooks";
import {
	getProductHomePath,
	ProductInternalName,
} from "@shared/navigation/SideMenu/productConfig";

// Private views
import Playground from "../playground/Playground";
import ChangelogPage from "@shared/changelog/ChangelogPage";
import { canSeeChangelog, isSalesMotionEnabled } from "@shared/config/featureFlags";
// Routes for each team
import { ctxReflectorRoutes } from "../ctxReflector/routes";
import { kontrolEdgeRoutes } from "../kontrolEdge/routes";
import { settingsRoutes } from "../settings/routes";
import StatusPage from "ctxReflector/components/Status/StatusPage";
import { ctxReflectorV2Routes } from "../ctxReflectorV2/routes";
import { insightRoutes } from "../insight/routes";
import { salesMotionRoutes } from "../salesMotion/routes";

/**
 * Post-login landing rule (priority order):
 *   Reflector v1 → `/` (StatusPage at index route)
 *   Kontrol Edge → Kontrol Home (`/kontrol`)
 *   Reflector V2 → Reflector V2 Overview
 *   Insight       → Insight Home
 *
 * Each target is the same `homePath` used by the SideMenu product dropdown
 * (`handleSelection` → `getProductHomePath`), so the menu's Home/Overview item
 * lights up automatically on landing and on dropdown switches.
 */
const ProductRedirect: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const userProfile = useAppSelector((state) => state.userProfile);

	const hasReflectorV1 = userProfile.ProductList.some(
		(product) => product.id === 1
	);
	const hasKontrol = userProfile.ProductList.some(
		(product) => product.name === "Kontrol Edge"
	);
	const hasReflectorV2 = userProfile.ProductList.some(
		(product) => product.name === "Reflector V2"
	);
	const hasInsight = userProfile.ProductList.some(
		(product) => product.name === "Insight"
	);

	useEffect(() => {
		if (location.pathname !== "/") return;
		// Reflector v1 owns "/" via the index route — no redirect needed
		if (hasReflectorV1) return;

		if (hasKontrol) {
			navigate(getProductHomePath(ProductInternalName.KontrolEdge), { replace: true });
		} else if (hasReflectorV2) {
			navigate(getProductHomePath(ProductInternalName.CtxReflectorV2), { replace: true });
		} else if (hasInsight) {
			navigate(getProductHomePath(ProductInternalName.Insight), { replace: true });
		}
	}, [location.pathname, hasReflectorV1, hasKontrol, hasReflectorV2, hasInsight, navigate]);

	return null;
};

export function PrivateRoutes() {
	// Read here and not in `ProductRedirect`: that component has its own copy,
	// but it is a sibling, not a parent, so its scope does not reach the route
	// table below.
	const { TenantType: tenantType } = useAppSelector(
		(state) => state.userProfile
	);

	return (
		<>
			{/* <IntercomProvider /> */}
			<ProductRedirect />
			<Routes>
				<Route
					element={
						// 1) Check the token in PrivateRouteWrapper
						// 2) If valid, render the global layout
						<PrivateRoute>
							<AppLayout />
						</PrivateRoute>
					}
				>
					{/* Within the layout, define the main private routes */}
					<Route index path="/" element={<StatusPage />} />
					<Route path="playground" element={<Playground />} />
					{/* Release notes — one flat feed. Deep-link an entry with
					    `/changelog#<entryKey>`. The route is absent, not merely
					    hidden, so direct URL access cannot reach it either.
					    `canSeeChangelog` is the same helper the footer and the
					    "New" tags use, so no surface can outlive the gate. */}
					{canSeeChangelog(tenantType) && (
						<Route path="changelog" element={<ChangelogPage />} />
					)}

					{/* Inject the routes defined by each team */}
					{ctxReflectorRoutes}
					{ctxReflectorV2Routes}
					{insightRoutes}
					{kontrolEdgeRoutes}
					{settingsRoutes}
					{/* KE Sales Motion (P-CTX-147): local prototype, absent (not just hidden)
					    unless its flag is on, and never in production. */}
					{isSalesMotionEnabled() && salesMotionRoutes}
				</Route>
			</Routes>
		</>
	);
}
